// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import { emoteEndpoints } from '@/modules/emote/emote.constants'
import type {
  TCreateEmoteRequest,
  TEditEmoteRequest,
  TEmote,
  TToggleEmoteActiveRequest,
} from '@/modules/emote/emote.types'

export async function getEmoteList(
  params: TAdminListParams
): Promise<TResponse<TEmote[]>> {
  const response = await api.get<TResponse<TEmote[]>>(
    emoteEndpoints.list(params)
  )

  return response.data
}

export async function getEmoteDetail(id: string): Promise<TEmote> {
  const response = await api.get<TResponse<TEmote>>(emoteEndpoints.detail(id))

  return response.data.data
}

// D6 presigned upload (same flow as team logos): presign → PUT to S3 → finalize.
async function uploadObject(file: File): Promise<string> {
  const contentType = file.type || 'application/octet-stream'
  const presign = await api.post<
    TResponse<{ upload_url: string; key: string }>
  >(emoteEndpoints.presign, { content_type: contentType })
  const { upload_url, key } = presign.data.data

  const put = await fetch(upload_url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
  if (!put.ok) throw new Error(`S3 upload failed (${put.status})`)
  return key
}

export async function createEmote(payload: TCreateEmoteRequest): Promise<void> {
  const key = await uploadObject(payload.file)
  await api.post<TResponse<TEmote>>(emoteEndpoints.finalize, {
    name: payload.name,
    key,
  })
}

export async function editEmote(payload: TEditEmoteRequest): Promise<void> {
  const body: { name?: string; key?: string } = {}
  if (payload.name) body.name = payload.name
  if (payload.file) body.key = await uploadObject(payload.file)

  await api.patch(emoteEndpoints.edit(payload.id), body)
}

export async function toggleEmoteActive(
  payload: TToggleEmoteActiveRequest
): Promise<TEmote> {
  const response = await api.patch<TResponse<TEmote>>(
    emoteEndpoints.active(payload.id),
    { is_active: payload.is_active }
  )

  return response.data.data
}

export async function deleteEmote(id: string): Promise<void> {
  await api.delete(emoteEndpoints.delete(id))
}
